// ────────────────────────────────────────────────────────────────────────────
// Live preview pane.
//
// This renders the *real* PDF and shows it in an iframe rather than mirroring
// the layout in HTML. An HTML mirror is faster to build and immediately starts
// lying — different text metrics, different wrapping, different pagination —
// which means every layout fault the operator is trying to spot is a fault the
// preview hides. The cost is a re-render per change, so the draft layout is
// debounced and the previous PDF stays on screen while the next one builds.
// ────────────────────────────────────────────────────────────────────────────
import { BlobProvider } from '@react-pdf/renderer';
import { cn } from '@my/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DocumentPdf } from '../render/DocumentPdf';
import type { DocumentData } from '../lib/documentData';
import type { DocumentTemplate } from '../lib/types';

interface Snapshot {
  template: DocumentTemplate;
  data: DocumentData;
}

/**
 * Publish `value` once `key` has been quiet for `delayMs`.
 *
 * Keyed on a serialised digest rather than object identity: callers rebuild
 * these objects on every render, so identity churns constantly while the thing
 * that matters — the design and the figures — usually has not changed. Without
 * the key, react-pdf would re-render the document on every keystroke.
 *
 * The initial value is published immediately, so a freshly mounted pane shows a
 * document instead of half a second of nothing.
 */
function useDebouncedSnapshot(value: Snapshot, key: string, delayMs: number) {
  const [settled, setSettled] = useState({ key, value });
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => {
    const timer = setTimeout(() => setSettled({ key, value: latest.current }), delayMs);
    return () => clearTimeout(timer);
  }, [key, delayMs]);
  return settled;
}

export interface PdfPreviewPaneProps {
  template: DocumentTemplate;
  data: DocumentData;
  className?: string;
}

export function PdfPreviewPane({ template, data, className }: PdfPreviewPaneProps) {
  // Both inputs are debounced. In the layout builder it is the design that
  // changes; in the invoice builder the design is fixed and the *figures*
  // change as the operator edits amounts and VAT rates. Either way a render per
  // keystroke is wasted work.
  // The digest includes the template's IDENTITY, not just its content.
  //
  // Content alone is not enough to decide whether the document on screen is
  // still the right one. The pane deliberately keeps showing the last rendered
  // PDF while the next one builds, so if nothing re-triggers a render the
  // iframe holds that blob indefinitely — and a Save whose layout matches what
  // was already on screen (the common case: save, having already seen the
  // result) produces no content change at all. The blob then outlives the
  // template it was rendered from, which reads as "it saved but the document is
  // still wrong until I refresh". `updatedUtc` moves on every save, so folding
  // it in guarantees a fresh render; `id` covers a hop to a different layout
  // that happens to be identical.
  const key = useMemo(
    () =>
      JSON.stringify({
        id: template.id,
        at: template.updatedUtc,
        layout: template.layout,
        data,
      }),
    [template.id, template.updatedUtc, template.layout, data],
  );
  const settled = useDebouncedSnapshot({ template, data }, key, 500);

  const element = useMemo(
    () => <DocumentPdf template={settled.value.template} data={settled.value.data} />,
    [settled],
  );

  const pending = key !== settled.key;

  return (
    <div className={cn('relative flex min-h-0 flex-1 flex-col bg-slate-100', className)}>
      <BlobProvider document={element}>
        {({ url, loading, error }) => (
          <PreviewFrame url={url} loading={loading} error={error} pending={pending} />
        )}
      </BlobProvider>
    </div>
  );
}

function PreviewFrame({
  url,
  loading,
  error,
  pending,
}: {
  url: string | null;
  loading: boolean;
  error: Error | null;
  pending: boolean;
}) {
  // Keep the last good URL on screen while the next render runs, so the pane
  // does not flash empty on every edit.
  const lastUrl = useRef<string | null>(null);
  if (url && !loading) lastUrl.current = url;
  const shown = url ?? lastUrl.current;

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-sm rounded-lg border border-red-200 bg-red-50 p-4 text-center">
          <p className="text-sm font-medium text-red-800">This layout could not be rendered</p>
          <p className="mt-1 text-xs text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {(loading || pending) && (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[11px] text-slate-500 shadow-sm">
          <Loader2 className="h-3 w-3 animate-spin" />
          Updating preview
        </div>
      )}
      {shown ? (
        // `#toolbar=0` hides the viewer chrome in Chromium so the pane reads as
        // a document preview rather than an embedded PDF reader.
        <iframe
          src={`${shown}#toolbar=0&navpanes=0&view=FitH`}
          title="Document preview"
          className="min-h-0 flex-1 border-0"
        />
      ) : (
        <div className="flex flex-1 items-center justify-center text-xs text-slate-400">
          Building preview…
        </div>
      )}
    </>
  );
}
